/**
 * Tables & charts executors — create_table, create_chart, sort_range, auto_filter.
 *
 * Each export is a CoreFn: (ctx, args?) => Promise<unknown> | unknown.
 *
 * Office.js Table reference:
 * https://learn.microsoft.com/en-us/javascript/api/excel/excel.table
 *
 * Office.js Chart reference:
 * https://learn.microsoft.com/en-us/javascript/api/excel/excel.chart
 */

/* global Excel */

import {
  getSheet,
  resolveExcelEnum,
  resolveRange,
  resolveSheetAddress,
} from "./utils";

// ---------------------------------------------------------------------------
// create_table
// ---------------------------------------------------------------------------

/**
 * Convert a range into a proper Excel Table (ListObject).
 *
 * Expected args:
 *   range:       string
 *   sheetName?:  string
 *   hasHeaders?: boolean (default true)
 *   name?:       string
 *   style?:      string — table style name
 */
export async function createTableCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const hasHeaders = args?.hasHeaders !== false; // default true
  const tableName = args?.name as string | undefined;
  const tableStyle = args?.style as string | undefined;

  if (!rangeAddr) {
    throw new Error("create_table requires a `range` argument.");
  }

  const { sheet, range } = resolveRange(ctx, rangeAddr, sheetName);

  const table = sheet.tables.add(range, hasHeaders);

  if (tableName) {
    table.name = tableName;
  }
  if (tableStyle) {
    table.style = tableStyle;
  }

  table.load("name,id,style");
  await ctx.sync();

  return {
    success: true,
    tableName: table.name,
    tableId: table.id,
    style: table.style,
    range: rangeAddr,
    hasHeaders,
  };
}

// ---------------------------------------------------------------------------
// create_chart
// ---------------------------------------------------------------------------

/**
 * Create a chart from a data range.
 *
 * Expected args:
 *   dataRange:  string
 *   sheetName?: string
 *   chartType:  string — e.g. "columnClustered", "line", "pie"
 *   title?:     string
 *   seriesBy?:  "auto" | "columns" | "rows"
 *   width?:     number (default 480)
 *   height?:    number (default 300)
 */
export async function createChartCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const dataRange = args?.dataRange as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const chartTypeStr = args?.chartType as string | undefined;
  const title = args?.title as string | undefined;
  const seriesByStr = args?.seriesBy as string | undefined;
  const width = (args?.width as number) ?? 480;
  const height = (args?.height as number) ?? 300;

  if (!dataRange) {
    throw new Error("create_chart requires a `dataRange` argument.");
  }
  if (!chartTypeStr) {
    throw new Error("create_chart requires a `chartType` argument.");
  }

  const chartType = resolveExcelEnum(
    Excel.ChartType,
    chartTypeStr,
    "chartType"
  );

  const seriesBy = resolveExcelEnum(
    Excel.ChartSeriesBy,
    seriesByStr ?? "auto",
    "seriesBy"
  );

  const { sheet, range } = resolveRange(ctx, dataRange, sheetName);

  const chart = sheet.charts.add(chartType, range, seriesBy);
  chart.width = width;
  chart.height = height;

  if (title) {
    chart.title.text = title;
    chart.title.visible = true;
  }

  chart.load("name,width,height");
  await ctx.sync();

  return {
    success: true,
    chartName: chart.name,
    chartType: chartTypeStr,
    dataRange,
    width: chart.width,
    height: chart.height,
    title: title ?? null,
  };
}

// ---------------------------------------------------------------------------
// sort_range
// ---------------------------------------------------------------------------

/**
 * Sort a range by one or more columns.
 *
 * Expected args:
 *   range:       string
 *   sheetName?:  string
 *   sortFields:  Array<{ columnIndex: number; ascending?: boolean }>
 *   hasHeaders?: boolean (default true)
 */
export function sortRangeCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const sortFields = args?.sortFields as
    | Array<{ columnIndex: number; ascending?: boolean }>
    | undefined;
  const hasHeaders = args?.hasHeaders !== false; // default true

  if (!rangeAddr) {
    throw new Error("sort_range requires a `range` argument.");
  }
  if (!sortFields || !Array.isArray(sortFields) || sortFields.length === 0) {
    throw new Error("sort_range requires a non-empty `sortFields` array.");
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  const fields: Excel.SortField[] = sortFields.map((sf) => ({
    key: sf.columnIndex,
    ascending: sf.ascending !== false, // default true
    sortOn: Excel.SortOn.value,
  }));

  range.sort.apply(fields, /* matchCase */ false, hasHeaders);

  return {
    success: true,
    range: rangeAddr,
    sortFieldCount: sortFields.length,
  };
}

// ---------------------------------------------------------------------------
// auto_filter
// ---------------------------------------------------------------------------

/**
 * Apply or clear auto-filter on a range.
 *
 * Expected args:
 *   range:        string
 *   sheetName?:   string
 *   action?:      "apply" | "clear" (default "apply")
 *   columnIndex?: number  — zero-based column for criteria
 *   criteria?:    { filterOn?: string; values?: string[]; count?: number }
 */
export async function autoFilterCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): Promise<unknown> {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const action = (args?.action as string) ?? "apply";

  if (!rangeAddr) {
    throw new Error("auto_filter requires a `range` argument.");
  }

  if (action === "clear") {
    // Remove the autoFilter from the sheet
    const target = resolveSheetAddress(rangeAddr, sheetName);
    getSheet(ctx, target.sheetName).autoFilter.remove();
    return { success: true, action: "clear" };
  }

  const { sheet, range } = resolveRange(ctx, rangeAddr, sheetName);

  const columnIndex = args?.columnIndex as number | undefined;
  const criteria = args?.criteria as
    | { filterOn?: string; values?: string[]; count?: number }
    | undefined;

  if (columnIndex !== undefined && criteria) {
    const filterOn = resolveExcelEnum(
      Excel.FilterOn,
      criteria.filterOn ?? "values",
      "filterOn"
    );

    const filterCriteria: Excel.FilterCriteria = { filterOn };

    if (criteria.values && filterOn === Excel.FilterOn.values) {
      filterCriteria.values = criteria.values;
    }
    if (criteria.count !== undefined) {
      filterCriteria.subField = criteria.count.toString();
    }

    sheet.autoFilter.apply(range, columnIndex, filterCriteria);
  } else {
    // Just enable auto-filter on the range with no criteria
    sheet.autoFilter.apply(range);
  }

  return { success: true, action: "apply", range: rangeAddr };
}
